import React, { useCallback, useEffect, useState } from "react";
import { Results, useResults, useSearch, Suggestions } from "@usereactify/search";

import { ProductCard } from "@app/scripts/reactify/results/ProductCard";
import { InstantSearchSuggestions } from "./InstantSearchSuggestions";
import { InstantSearchCollections } from "./InstantSearchCollections";
import { InstantSearchResultsFallback } from "./InstantSearchResultsFallback";
import { debounce } from "lodash";

const DEBOUNCE_TIME = 100;

export const InstantSearch: React.FC = () => {
  const { setSearchTerm, searchTerm, submitSearchTerm, showInstantSearchResults, setShowInstantSearchResults } =
    useSearch();
  const { resultStats } = useResults();
  const [pageSize, setPageSize] = useState(4);
  const [delayedShowResults, setDelayedShowResults] = useState(showInstantSearchResults);

  useEffect(() => {
    setTimeout(() => {
      setDelayedShowResults(showInstantSearchResults);
    }, DEBOUNCE_TIME);
  }, [showInstantSearchResults]);

  const onSearchEmpty = () => {
    setSearchTerm("");
    const inputs = document.querySelectorAll("[data-instant-search-form] [data-instant-search-input]");
    inputs.forEach((elem) => (elem.value = ""));
    setShowInstantSearchResults(false);
  };

  const onSearchDebounce = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setShowInstantSearchResults(true);
    }, DEBOUNCE_TIME),
    [setSearchTerm, setShowInstantSearchResults]
  );

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      submitSearchTerm((formData.get("searchTerm") as string | undefined) || "");
    },
    [submitSearchTerm, searchTerm]
  );

  const handleKeyDown = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    onSearchDebounce(e);
    if (!value) {
      onSearchEmpty();
    }
  };

  const handleFocus = () => {
    setShowInstantSearchResults(true);
    document.querySelector("main-header")?.classList.add("instant-search__active");
  };

  const handleClick = () => {
    setDelayedShowResults(true);
    document.querySelector("main-header")?.classList.add("instant-search__active");
  };

  const handleClose = () => {
    onSearchEmpty();
    setDelayedShowResults(false);
    document.querySelector("main-header")?.classList.remove("instant-search__active");
  };

  useEffect(() => {
    const searchForms = document.querySelectorAll("[data-instant-search-form]");

    searchForms.forEach((form) => {
      const input = form?.querySelector("[data-instant-search-input]");
      const close = form?.querySelector("[data-close-instant-search]");
      input?.addEventListener?.("keydown", handleKeyDown);
      input?.addEventListener?.("click", handleClick);
      input?.addEventListener?.("focus", handleFocus);
      close?.addEventListener?.("click", handleClose);
      form?.addEventListener?.("submit", handleSubmit);
    });

    return () => {
      searchForms.forEach((form) => {
        const input = form?.querySelector("[data-instant-search-input]");
        const close = form?.querySelector("[data-close-instant-search]");
        input?.removeEventListener?.("keydown", handleKeyDown);
        input?.removeEventListener?.("click", handleClick);
        input?.removeEventListener?.("focus", handleFocus);
        close?.addEventListener?.("click", handleClose);
        form?.removeEventListener?.("submit", handleSubmit);
      });
    };
  }, []);

  return (
    <div className="instant-search">
      {/* Instant Search Results */}
      {delayedShowResults && (
        <>
          <div className="instant-search__results-overlay" onClick={handleClose}></div>

          <div className="instant-search__results">
            <div className="instant-search__results-container">
              <div className="instant-search__results-container-left">
                <div>
                  <Suggestions field="title" render={InstantSearchSuggestions} />
                </div>

                <div>
                  <InstantSearchCollections />
                </div>
              </div>

              <div className="instant-search__results-container-right">
                <Results
                  pageSize={pageSize}
                  listClassName="instant-search__results-grid"
                  listStyle={{}}
                  renderResultCardProduct={ProductCard}
                  renderNoResults={InstantSearchResultsFallback}
                />

                {resultStats?.numberOfResults && resultStats.numberOfResults > pageSize ? (
                  <div className="instant-search__results-view-all">
                    <button onClick={() => submitSearchTerm(searchTerm)}>
                      View {resultStats?.numberOfResults} result{resultStats.numberOfResults > pageSize ? "s" : ""}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
